/**
 * components/settings/SettingsSecuritySection.tsx
 *
 * Section Sécurité des paramètres.
 * Permet à l'utilisateur de changer son mot de passe.
 * Updated: suppression du titre h2 (déplacé dans SettingsSection).
 * Updated: suppression du sous-header icône KeyRound + "Mot de passe" redondant
 *   avec la colonne gauche ; champs contraints à max-w-md ; bouton renommé
 *   "Modifier le mot de passe" pour différencier de la SaveBar du profil.
 */

import { useState } from 'react';
import PasswordInput from '../ui/PasswordInput';
import PasswordStrengthIndicator from '../ui/PasswordStrengthIndicator';
import Button from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

export default function SettingsSecuritySection() {
  const { updatePassword } = useAuth();
  const { showToast } = useToast();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [saving, setSaving] = useState(false);

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const isWeak = newPassword.length > 0 && newPassword.length < 8;
  const canSubmit = newPassword.length >= 8 && confirmPassword === newPassword && !saving;

  const handleConfirmChange = (value: string) => {
    setConfirmPassword(value);
    if (value.length > 0 && newPassword !== value) {
      setConfirmError('Les mots de passe ne correspondent pas');
    } else {
      setConfirmError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSaving(true);
    const { error } = await updatePassword(newPassword);
    setSaving(false);

    if (error) {
      showToast('Une erreur est survenue. Veuillez réessayer.', 'error');
    } else {
      showToast('Mot de passe modifié avec succès.', 'success');
      setNewPassword('');
      setConfirmPassword('');
      setConfirmError('');
    }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-6 shadow-soft">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="max-w-md">
          <PasswordInput
            label="Nouveau mot de passe"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
          />
          <PasswordStrengthIndicator password={newPassword} />
          {isWeak && (
            <p className="mt-1.5 text-xs text-error-600 font-medium">
              Le mot de passe doit contenir au moins 8 caractères
            </p>
          )}
        </div>

        <div className="max-w-md">
          <PasswordInput
            label="Confirmer le mot de passe"
            value={confirmPassword}
            onChange={(e) => handleConfirmChange(e.target.value)}
            autoComplete="new-password"
            error={mismatch ? confirmError : undefined}
          />
        </div>

        <div className="pt-1">
          <Button
            type="submit"
            disabled={!canSubmit}
            loading={saving}
          >
            Modifier le mot de passe
          </Button>
        </div>
      </form>
    </div>
  );
}
